import { Heading as AriaHeading } from "react-aria-components"

import CoachingExamplePlayer from "@/components/chat/CoachingExamplePlayer"
import type { CoachingContent } from "@/utils/conversation-state-builder"

// The structured coaching-card renderer, shared by replay (read-only) and the
// live `/chat` surface so both paint a coach_dialogue_page's `content`
// identically. Stateless by design — it reads only `content` — so promoting it
// out of CoachingSurface's read-only branch keeps replay output byte-identical
// while letting the live path render framing / interrupt cards the same way.
export interface CoachingContentRendererProps {
  content?: CoachingContent | string
  // "hero" scales the title + body up for the full-screen takeover card. Default
  // keeps the inline/replay sizing so replay output stays byte-identical.
  emphasis?: "default" | "hero"
}

export default function CoachingContentRenderer({
  content,
  emphasis = "default",
}: CoachingContentRendererProps) {
  const hero = emphasis === "hero"
  const titleCls = hero
    ? "text-xl leading-snug font-semibold text-gray-900"
    : "text-sm leading-relaxed font-medium text-gray-900"
  const bodyCls = hero
    ? "text-base leading-relaxed text-gray-700"
    : "text-sm leading-relaxed text-gray-700"
  const listCls = `list-disc space-y-1 pl-5 ${bodyCls}`

  if (typeof content === "string") {
    return <p className={bodyCls}>{content}</p>
  }

  return (
    <div className={hero ? "space-y-3" : "space-y-2"}>
      {content?.title && (
        <AriaHeading slot="title" className={titleCls}>
          {content.title}
        </AriaHeading>
      )}
      {Array.isArray(content?.instructions) &&
        content.instructions.length === 1 && (
          <p className={bodyCls}>{content.instructions[0]}</p>
        )}
      {Array.isArray(content?.instructions) &&
        content.instructions.length > 1 && (
          <ul className={listCls}>
            {content.instructions.map((it, idx) => (
              <li key={idx}>{it}</li>
            ))}
          </ul>
        )}
      {Array.isArray(content?.goals) && content.goals.length > 0 && (
        <div className="mt-2 space-y-1">
          {content.goals.map((g) => (
            <div key={g.id} className="rounded-md border border-gray-200 p-2">
              <div className="text-sm font-medium text-gray-900">{g.title}</div>
              {g.description && (
                <div className="text-xs text-gray-700">{g.description}</div>
              )}
            </div>
          ))}
        </div>
      )}
      {Array.isArray(content?.tips) && content.tips.length > 0 && (
        <div>
          <div
            className={
              hero
                ? "text-sm font-semibold text-gray-900"
                : "text-xs font-semibold text-gray-900"
            }
          >
            Tips
          </div>
          <ul className={listCls}>
            {content.tips.map((t, idx) => (
              <li key={idx}>{t}</li>
            ))}
          </ul>
        </div>
      )}
      {Array.isArray(content?.examples) && content.examples.length > 0 && (
        <div>
          <div
            className={
              hero
                ? "text-sm font-semibold text-gray-900"
                : "text-xs font-semibold text-gray-900"
            }
          >
            Examples
          </div>
          <ul className={listCls}>
            {content.examples.map((ex, idx) => (
              <li key={idx}>{ex}</li>
            ))}
          </ul>
        </div>
      )}
      {content?.example_turns && (
        <CoachingExamplePlayer example={content.example_turns} />
      )}
    </div>
  )
}
